
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type ProceduralSkyShaderUniforms = {
	uProjection: WebGLUniformLocation,
	uView: WebGLUniformLocation,
	uSunDirection: WebGLUniformLocation,
	uSunColor: WebGLUniformLocation,
	uSunIntensity: WebGLUniformLocation,
	uTintColor: WebGLUniformLocation,
	uFogColor: WebGLUniformLocation,
	uHorizonMist: WebGLUniformLocation,
	uExposure: WebGLUniformLocation,
	uTime: WebGLUniformLocation,
	uCloudColor: WebGLUniformLocation,
	uCloudShadowColor: WebGLUniformLocation,
	uCloudCoverage: WebGLUniformLocation,
	uCloudDensity: WebGLUniformLocation,
	uCloudScale: WebGLUniformLocation,
	uCloudWind: WebGLUniformLocation,
	uShowSun: WebGLUniformLocation,
	uShowStars: WebGLUniformLocation,
	uShowMoon: WebGLUniformLocation,
	uShowClouds: WebGLUniformLocation
};

export type ProceduralSkyShaderAttributes = {
	aPosition: number,
	vWorldDir: number
};

export type ProceduralSkyShaderSettings = string[];

export class ProceduralSkyShader extends ShaderBase<ProceduralSkyShaderUniforms, ProceduralSkyShaderAttributes> {

    private static fragmentShaderSource = `
// Analytical atmospheric scattering sky.
//
// Single-scattering integration of Rayleigh + Mie through a spherical
// atmosphere shell, evaluated per-fragment along the view ray. Based on the
// well-known Nishita / "glsl-atmosphere" formulation: cheap, robust, and
// produces correct horizon glow, blue zenith, sunset colours, and a sun
// disc that the rest of the engine's lighting can be wired to.
//
// Output is in linear (HDR-ish) light. A small exposure tonemap is applied
// at the end to keep the output in a sane range for the existing
// non-tonemapped final composite path.
//
// Optional layers, each gated by a feature flag uniform:
//   - sun disc + halo
//   - procedural starfield (faded out by sky luminance, so only visible at night)
//   - simple moon disc on the antisolar direction
//   - 2D FBM dome clouds (drift over time, sun-tinted at golden hour)
// Disabled layers cost only the flag check.

// --- begin: atmosphere.glsl ---
// Single-scattering Rayleigh + Mie integration through a spherical atmosphere
// shell. Shared by:
//   - procedural-sky: sky dome colour as a function of view direction.
//   - aerial-perspective: distance-based haze that blends opaque scene pixels
//     toward the same scattering value, so far-LOD shape drift, tile-edge
//     mismatches, and the hard horizon clip all wash into atmosphere.
//
// Inputs use the well-known Nishita / "glsl-atmosphere" formulation. The
// virtual observer sits just above the planet surface — the shader's actual
// world position doesn't matter because scattering is a pure function of the
// view direction relative to the planet centre.

const float ATMOSPHERE_PI = 3.14159265358979;

const float PLANET_RADIUS = 6371000.0;
const float ATMOS_RADIUS  = 6471000.0;
const vec3  RAY_BETA      = vec3(5.5e-6, 13.0e-6, 22.4e-6); // Rayleigh scattering coefficients
const vec3  MIE_BETA      = vec3(21e-6);                    // Mie scattering coefficient
const float HEIGHT_RAY    = 8000.0;
const float HEIGHT_MIE    = 1200.0;
const float G             = 0.758; // Mie phase asymmetry

const int STEPS_PRIMARY = 16;
const int STEPS_LIGHT   = 8;

// Returns t for the far intersection of a ray with a sphere centred at the
// origin, or -1.0 if there is no intersection.
float raySphereFar(vec3 origin, vec3 dir, float radius) {
	float b = dot(origin, dir);
	float c = dot(origin, origin) - radius * radius;
	float d = b * b - c;
	if (d < 0.0) return -1.0;
	return -b + sqrt(d);
}

// Atmospheric scattering integrated along the primary ray, capped at maxDist
// metres. The sky dome passes a huge value (full column to outer atmosphere);
// aerial perspective passes the camera-to-pixel distance so opaque surfaces
// only receive in-scattering from the air actually between camera and
// surface — without the cap a 2km mountain peak picks up the same Mie sun
// peak as the 100km sky behind it. Caller multiplies by uSunIntensity.
vec3 atmosphere(vec3 rayDir, vec3 rayOrigin, vec3 sunDir, float maxDist) {
	float atmosDist = raySphereFar(rayOrigin, rayDir, ATMOS_RADIUS);
	if (atmosDist < 0.0) return vec3(0.0);
	atmosDist = min(atmosDist, maxDist);

	float stepSize = atmosDist / float(STEPS_PRIMARY);

	float mu = dot(rayDir, sunDir);
	float mu2 = mu * mu;
	float gg = G * G;
	float phaseRay = 3.0 / (16.0 * ATMOSPHERE_PI) * (1.0 + mu2);
	float phaseMie = 3.0 / (8.0 * ATMOSPHERE_PI)
		* ((1.0 - gg) * (mu2 + 1.0))
		/ (pow(1.0 + gg - 2.0 * mu * G, 1.5) * (2.0 + gg));

	vec3 sumRay = vec3(0.0);
	vec3 sumMie = vec3(0.0);
	float odRay = 0.0;
	float odMie = 0.0;

	for (int i = 0; i < STEPS_PRIMARY; i++) {
		vec3 sp = rayOrigin + rayDir * (float(i) + 0.5) * stepSize;
		float h = length(sp) - PLANET_RADIUS;
		float hr = exp(-h / HEIGHT_RAY) * stepSize;
		float hm = exp(-h / HEIGHT_MIE) * stepSize;
		odRay += hr;
		odMie += hm;

		// Light ray from sample point toward the sun.
		float lDist = raySphereFar(sp, sunDir, ATMOS_RADIUS);
		float lStep = lDist / float(STEPS_LIGHT);
		float lOdRay = 0.0;
		float lOdMie = 0.0;
		bool blocked = false;
		for (int j = 0; j < STEPS_LIGHT; j++) {
			vec3 lp = sp + sunDir * (float(j) + 0.5) * lStep;
			float lh = length(lp) - PLANET_RADIUS;
			if (lh < 0.0) { blocked = true; break; }
			lOdRay += exp(-lh / HEIGHT_RAY) * lStep;
			lOdMie += exp(-lh / HEIGHT_MIE) * lStep;
		}

		if (!blocked) {
			vec3 tau = RAY_BETA * (odRay + lOdRay) + MIE_BETA * 1.1 * (odMie + lOdMie);
			vec3 attn = exp(-tau);
			sumRay += hr * attn;
			sumMie += hm * attn;
		}
	}

	return phaseRay * RAY_BETA * sumRay + phaseMie * MIE_BETA * sumMie;
}

// --- end: atmosphere.glsl ---

out vec4 fragColor;
in vec3 vWorldDir;

uniform vec3 uSunDirection;   // direction TOWARD the sun, world space, normalized
uniform vec3 uSunColor;       // sun radiance multiplier (rgb)
uniform float uSunIntensity;  // overall scattering intensity
uniform vec3 uTintColor;      // multiplicative tint (driven by DayNightCycle)
uniform vec3 uFogColor;       // colour to lerp toward at the horizon
uniform float uHorizonMist;   // 0 = no mist, 1 = full mist at horizon
uniform float uExposure;      // simple exposure for the final tonemap
uniform float uTime;          // engine time in seconds, drives cloud drift

// Cloud parameters.
uniform vec3 uCloudColor;       // base (lit) cloud colour
uniform vec3 uCloudShadowColor; // dark side / under-cloud colour
uniform float uCloudCoverage;   // 0 = clear sky, 1 = fully overcast
uniform float uCloudDensity;    // 0..1 opacity multiplier for the cloud layer
uniform float uCloudScale;      // controls cloud size (smaller = bigger clouds)
uniform vec2 uCloudWind;        // drift velocity in cloud-uv space per second

// Feature toggles (0 = off, 1 = on). Driven from ProceduralSky.ts.
uniform float uShowSun;
uniform float uShowStars;
uniform float uShowMoon;
uniform float uShowClouds;

// Cheap 3D hash → [0,1). Stable per integer cell, used for the starfield.
float hash13(vec3 p) {
	p = fract(p * 0.1031);
	p += dot(p, p.yzx + 33.33);
	return fract((p.x + p.y) * p.z);
}

// Cheap 2D hash → [0,1). Used as the base for the cloud value-noise.
float hash21(vec2 p) {
	p = fract(p * vec2(123.34, 456.21));
	p += dot(p, p + 45.32);
	return fract(p.x * p.y);
}

// Smooth 2D value noise (interpolated lattice hash).
float valueNoise2(vec2 p) {
	vec2 i = floor(p);
	vec2 f = fract(p);
	vec2 u = f * f * (3.0 - 2.0 * f);
	float a = hash21(i);
	float b = hash21(i + vec2(1.0, 0.0));
	float c = hash21(i + vec2(0.0, 1.0));
	float d = hash21(i + vec2(1.0, 1.0));
	return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// 5-octave FBM, normalised to roughly [0, 1).
float cloudFbm(vec2 p) {
	float v = 0.0;
	float a = 0.5;
	float total = 0.0;
	for (int i = 0; i < 5; i++) {
		v += a * valueNoise2(p);
		total += a;
		p = p * 2.02 + vec2(11.7, 3.4);
		a *= 0.5;
	}
	return v / total;
}

// Sample a 2D cloud field by projecting the view direction onto a virtual
// horizontal cloud plane. dir.xz / dir.y is the standard "infinite plane at
// height 1" parametrisation; the max() guard prevents the divide from
// exploding near the horizon.
vec2 cloudPlaneCoords(vec3 dir) {
	float y = max(dir.y, 0.05);
	return dir.xz / y;
}

// Procedural starfield: each grid cell of the sphere either has a star or
// doesn't. Brightness, hue, and a small temperature shift are also hashed
// from the cell. Cheaper than a texture lookup and doesn't repeat.
vec3 starField(vec3 dir) {
	vec3 cell = floor(dir * 400.0);
	float h1 = hash13(cell);
	float threshold = 0.9965;
	if (h1 < threshold) return vec3(0.0);
	float b = pow((h1 - threshold) / (1.0 - threshold), 3.0);
	float temp = hash13(cell + 7.31);
	vec3 tint = mix(vec3(1.0, 0.92, 0.78), vec3(0.78, 0.88, 1.0), temp);
	return tint * b * 4.0;
}

void main() {
	vec3 dir = normalize(vWorldDir);
	vec3 sunDir = normalize(uSunDirection);

	// Place the virtual observer just above the planet surface at the planet
	// centre's vertical axis. The ray's actual world position doesn't matter
	// for an analytical sky — only its direction does.
	vec3 origin = vec3(0.0, PLANET_RADIUS + 1.0, 0.0);

	vec3 atm = uSunIntensity * atmosphere(dir, origin, sunDir, 1e20);

	// Night factor based on sun elevation, NOT sky luminance: when the sun is
	// well below the horizon (sunDir.y < ~-0.05) we're in true night, and
	// stars + moon should be at full brightness. As the sun rises through the
	// horizon they fade out smoothly. Decoupling from sky luminance avoids
	// the chicken-and-egg problem where dimming the sky tint also dims the
	// threshold the stars need to pass.
	float nightFactor = 1.0 - smoothstep(-0.08, 0.08, sunDir.y);

	// Above-horizon factor: stars and moon only render in the upper hemisphere
	// (with a soft fade across the horizon line) — there's no ground geometry
	// to occlude them otherwise.
	float aboveHorizon = smoothstep(0.0, 0.05, dir.y);

	// Horizon mist on the atmosphere only: lerp toward the engine's fog
	// colour for the lowest few degrees of the dome so the procedural sky
	// reads as continuous with the volumetric fog pass.
	float horizonT = smoothstep(-0.05, 0.20, dir.y);
	atm = mix(mix(uFogColor, atm, horizonT), atm, 1.0 - uHorizonMist);

	// Per-channel Reinhard tonemap on the atmosphere only — keeps chroma
	// from washing out to white as the sky gets bright at noon, while
	// still bringing the linear scattering values into [0,1).
	atm *= uExposure;
	atm = atm / (vec3(1.0) + atm);

	// Apply the day/night tint ONLY to the atmospheric component. Celestial
	// bodies (sun, moon, stars) are not part of the atmosphere and should not
	// be dimmed by the night sky tint — otherwise we'd never see stars at
	// midnight when the tint is dark.
	vec3 col = atm * uTintColor;

	// --- Stars (drawn before sun/moon discs so the discs sit on top) ---
	if (uShowStars > 0.5) {
		col += starField(dir) * nightFactor * aboveHorizon;
	}

	// --- Moon disc on the antisolar direction ---
	// Drawn AFTER stars so it occludes them (mix() replaces, not add() blends).
	if (uShowMoon > 0.5) {
		vec3 moonDir = -sunDir;
		float moonDot = dot(dir, moonDir);
		float moonDisc = smoothstep(0.99955, 0.99985, moonDot);
		float moonGlow = pow(max(moonDot, 0.0), 512.0) * 0.35;

		// Approximate surface position on the moon: project the view direction
		// onto the plane perpendicular to moonDir, giving 2D coords across the
		// disc that we can later use for a texture sample.
		vec3 moonPlane = dir - moonDir * moonDot;
		// Phase: soft terminator from a fixed in-plane "lit" direction.
		vec3 lit = normalize(vec3(0.6, 0.2, 0.8));
		float phase = smoothstep(-0.15, 0.55, dot(moonPlane, lit));
		vec3 moonSurface = mix(vec3(0.06, 0.07, 0.10), vec3(0.95, 0.96, 1.00), phase);

		float moonAlpha = moonDisc * nightFactor * aboveHorizon;
		col = mix(col, moonSurface, moonAlpha);
		col += moonGlow * vec3(0.7, 0.75, 0.9) * nightFactor * aboveHorizon;
	}

	// --- Sun disc + halo (only when sun is above horizon) ---
	if (uShowSun > 0.5) {
		float sunAbove = smoothstep(-0.05, 0.05, sunDir.y);
		float sunDot = dot(dir, sunDir);
		float sunDisc = smoothstep(0.99970, 0.99995, sunDot);
		float sunHalo = pow(max(sunDot, 0.0), 256.0) * 0.6;
		col += (sunDisc * 30.0 + sunHalo) * uSunColor * sunAbove;
	}

	// --- 2D FBM dome clouds ---
	// Drawn last so they occlude the sun, moon, and stars (realistic — clouds
	// are much closer to the observer than any celestial body).
	if (uShowClouds > 0.5 && dir.y > 0.0) {
		vec2 uv = cloudPlaneCoords(dir) * uCloudScale + uCloudWind * uTime;

		// Domain warp: perturb the sample coords by a low-octave FBM so the
		// cloud silhouettes get organic billowy shapes instead of streaks.
		vec2 warp = vec2(
			cloudFbm(uv * 0.6 + vec2(0.0, uTime * 0.004)),
			cloudFbm(uv * 0.6 + vec2(7.3, uTime * 0.004))
		);
		warp = (warp - 0.5) * 1.6;
		vec2 wuv = uv + warp;

		float n = cloudFbm(wuv);

		// Coverage cutoff: only the upper part of the noise becomes cloud.
		// Higher uCloudCoverage pulls the cutoff down so more of the sky fills.
		float cutoff = 1.0 - clamp(uCloudCoverage, 0.0, 1.0);
		float cloud = smoothstep(cutoff, min(cutoff + 0.25, 1.0), n);

		// Fade clouds out as they approach the horizon (perspective foreshortening
		// would otherwise stretch them into ugly streaks at the rim).
		float horizonFade = smoothstep(0.02, 0.18, dir.y);
		cloud *= horizonFade;
		float opacity = clamp(cloud * uCloudDensity, 0.0, 1.0);

		// Self-shadow term with stronger contrast: bias toward the lit side so
		// cloud edges pop instead of fading to uniform grey.
		float thickness = smoothstep(cutoff, 1.0, n);
		thickness = pow(thickness, 0.65);

		// Directional shading: sample the cloud field a short step in the
		// horizontal sun direction. Where this point is denser than the
		// forward sample we're on the sun-facing side; where lighter we're in
		// the lee. Cheap fake-3D without volumetric raymarching.
		vec2 sunHoriz = sunDir.xz;
		float sunHorizLen = length(sunHoriz);
		if (sunHorizLen > 1e-4) sunHoriz /= sunHorizLen;
		float nForward = cloudFbm(wuv + sunHoriz * 0.35);
		float facing = clamp((n - nForward) * 2.5 + 0.5, 0.0, 1.0);

		// Sun-tint the lit side: when looking near the sun, the cloud edges
		// catch warm light (golden hour effect). Otherwise blend toward the
		// configured cloud colour.
		float sunDot = max(dot(dir, sunDir), 0.0);
		float silver = pow(sunDot, 8.0);
		vec3 lit = mix(uCloudColor, uSunColor, silver * 0.6);

		// Day/night gating: at night clouds are weakly moon-lit and read as
		// dim grey overcast — visible against the dark sky but clearly not
		// daytime clouds.
		float sunUp = smoothstep(-0.1, 0.15, sunDir.y);
		lit *= mix(0.10, 1.0, sunUp);
		vec3 shadow = uCloudShadowColor * mix(0.06, 1.0, sunUp);

		// Compose: thickness drives base lit/shadow gradient, then the
		// directional facing term darkens the lee side independently so even
		// thin clouds get a sun-side / shade-side split.
		vec3 cloudCol = mix(lit, shadow, thickness);
		cloudCol *= mix(0.55, 1.15, facing);

		col = mix(col, cloudCol, opacity);
	}

	fragColor = vec4(col, 1.0);
}
`;

    private static vertexShaderSource = `
layout(location = 0) in vec3 aPosition;

uniform mat4 uProjection;
uniform mat4 uView;

out vec3 vWorldDir;

void main() {
	// Cube vertex positions (in unit object space) double as world-space view
	// directions: the standard skybox trick. The view matrix's translation is
	// stripped via mat3() so the cube stays centred on the camera. The caller
	// disables depth writes with depthMask(false) so any later geometry wins.
	vWorldDir = aPosition;
	gl_Position = uProjection * mat4(mat3(uView)) * vec4(aPosition, 1.0);
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: ProceduralSkyShaderUniformsContainer;
    public attributesContainer: ProceduralSkyShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: ProceduralSkyShaderSettings) {
        super(ctx, ProceduralSkyShader.fragmentShaderSource, ProceduralSkyShader.vertexShaderSource, settings);
        this.uniformsContainer = new ProceduralSkyShaderUniformsContainer(ctx, this);
        this.attributesContainer = new ProceduralSkyShaderShaderAttributesContainer(ctx, this);
    }
}

class ProceduralSkyShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: ProceduralSkyShader) {
        super(ctx);
    }

    public setProjection(value: mat4 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uProjection', this.shader.uniforms.uProjection, value, 'mat4');
        return this;
    }

    public setView(value: mat4 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uView', this.shader.uniforms.uView, value, 'mat4');
        return this;
    }

    public setSunDirection(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uSunDirection', this.shader.uniforms.uSunDirection, value, 'vec3');
        return this;
    }

    public setSunColor(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uSunColor', this.shader.uniforms.uSunColor, value, 'vec3');
        return this;
    }

    public setSunIntensity(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uSunIntensity', this.shader.uniforms.uSunIntensity, value, 'float');
        return this;
    }

    public setTintColor(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uTintColor', this.shader.uniforms.uTintColor, value, 'vec3');
        return this;
    }

    public setFogColor(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uFogColor', this.shader.uniforms.uFogColor, value, 'vec3');
        return this;
    }

    public setHorizonMist(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uHorizonMist', this.shader.uniforms.uHorizonMist, value, 'float');
        return this;
    }

    public setExposure(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uExposure', this.shader.uniforms.uExposure, value, 'float');
        return this;
    }

    public setTime(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uTime', this.shader.uniforms.uTime, value, 'float');
        return this;
    }

    public setCloudColor(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudColor', this.shader.uniforms.uCloudColor, value, 'vec3');
        return this;
    }

    public setCloudShadowColor(value: vec3 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudShadowColor', this.shader.uniforms.uCloudShadowColor, value, 'vec3');
        return this;
    }

    public setCloudCoverage(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudCoverage', this.shader.uniforms.uCloudCoverage, value, 'float');
        return this;
    }

    public setCloudDensity(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudDensity', this.shader.uniforms.uCloudDensity, value, 'float');
        return this;
    }

    public setCloudScale(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudScale', this.shader.uniforms.uCloudScale, value, 'float');
        return this;
    }

    public setCloudWind(value: vec2 | number[]) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uCloudWind', this.shader.uniforms.uCloudWind, value, 'vec2');
        return this;
    }

    public setShowSun(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uShowSun', this.shader.uniforms.uShowSun, value, 'float');
        return this;
    }

    public setShowStars(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uShowStars', this.shader.uniforms.uShowStars, value, 'float');
        return this;
    }

    public setShowMoon(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uShowMoon', this.shader.uniforms.uShowMoon, value, 'float');
        return this;
    }

    public setShowClouds(value: number) : ProceduralSkyShaderUniformsContainer {
        this.setValue('uShowClouds', this.shader.uniforms.uShowClouds, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [

        ];
    }
}

class ProceduralSkyShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: ProceduralSkyShader) {
        super(ctx);
    }

    public setPosition(settings: { length: number, offset: number, itemSize: number }) : ProceduralSkyShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldDir(settings: { length: number, offset: number, itemSize: number }) : ProceduralSkyShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldDir, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}